/**
 * Professor Show Page - Alpine.js Implementation
 * Main component for professor show page
 */

import { initComments } from '../../components/comments.js';

// Define the component factory function
const professorShowComponent = (config) => ({
    // Configuration
    config: config || {},
    professorId: config?.professorId || null,

    // Main initialization
    init() {
        this.setupEventListeners();
        this.initializeAnimations();
        this.initializeComments();
    },

    // Setup global event listeners
    setupEventListeners() {
        // Listen for auth events
        window.addEventListener('auth-success', () => {
            window.location.reload();
        });

        // Listen for review updates
        window.addEventListener('review-submitted', (event) => {
            if (event.detail?.success) {
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            }
        });
    },

    // Initialize animations
    initializeAnimations() {
        // Add animation delays to review cards
        this.$nextTick(() => {
            const reviewCards = document.querySelectorAll('.review-item');
            reviewCards.forEach((card, index) => {
                card.style.animationDelay = `${index * 0.1}s`;
                card.classList.add('animate-fade-in');
            });
        });
    },

    // Initialize comments system
    initializeComments() {
        // Initialize immediately - don't wait for Alpine's nextTick
        // This ensures the form submit handler is attached before any form submission
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                initComments();
            });
        } else {
            // DOM is already ready
            initComments();
        }
    },

    // Utility Functions
    getCsrfToken() {
        return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    },

    // Copy email to clipboard
    copyEmail(email) {
        if (!email) {
            console.error('No email provided');
            return;
        }

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(email)
                .then(() => {
                    // Dispatch event to show notification
                    const event = new CustomEvent('notify', {
                        detail: { type: 'success', message: 'ایمیل کپی شد' }
                    });
                    window.dispatchEvent(event);
                })
                .catch((err) => {
                    console.error('Failed to copy email:', err);
                    alert('خطا در کپی ایمیل');
                });
        } else {
            // Fallback for HTTP or browsers without clipboard API
            const textArea = document.createElement('textarea');
            textArea.value = email;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            try {
                const successful = document.execCommand('copy');
                if (successful) {
                    const event = new CustomEvent('notify', {
                        detail: { type: 'success', message: 'ایمیل کپی شد' }
                    });
                    window.dispatchEvent(event);
                } else {
                    throw new Error('execCommand failed');
                }
            } catch (err) {
                console.error('Failed to copy email:', err);
                alert('ایمیل: ' + email);
            }

            document.body.removeChild(textArea);
        }
    }
});

// Expose on window so Alpine x-data="professorShow({...})" can find it
// (Alpine checks window for functions when evaluating x-data expressions)
window.professorShow = professorShowComponent;

// Also register via Alpine.data if Alpine is already available
if (typeof Alpine !== 'undefined' && Alpine.data) {
    Alpine.data('professorShow', professorShowComponent);
}

// Global functions for modal actions
window.handleProfessorReviewAction = (professorId) => {
    if (!professorId) {
        // Try to get from Alpine component
        const component = document.querySelector('[x-data*="professorShow"]')?.__x?.$data;
        professorId = component?.professorId || null;
    }

    if (!professorId) {
        // Try to extract from URL
        const urlMatch = window.location.pathname.match(/\/professors\/(\d+)/);
        if (urlMatch) {
            professorId = parseInt(urlMatch[1]);
        }
    }

    if (!professorId) {
        console.error('Professor ID not found. Cannot open review modal.');
        return;
    }

    if (window.Livewire && typeof window.Livewire.dispatch === 'function') {
        window.Livewire.dispatch('openModal', {
            component: 'features.professors.professor-review-form',
            arguments: { professor: professorId }
        });
    } else {
        console.error('Livewire is not available. Cannot open review modal.');
    }
};

window.handleProfessorEmailAction = (professorId) => {
    if (!professorId) {
        // Try to get from Alpine component
        const component = document.querySelector('[x-data*="professorShow"]')?.__x?.$data;
        professorId = component?.professorId || null;
    }

    if (!professorId) {
        // Try to extract from URL
        const urlMatch = window.location.pathname.match(/\/professors\/(\d+)/);
        if (urlMatch) {
            professorId = parseInt(urlMatch[1]);
        }
    }

    if (!professorId) {
        console.error('Professor ID not found. Cannot open email modal.');
        return;
    }

    if (window.Livewire && typeof window.Livewire.dispatch === 'function') {
        window.Livewire.dispatch('openModal', {
            component: 'features.professors.professor-email-modal',
            arguments: { professor: professorId }
        });
    } else {
        console.error('Livewire is not available. Cannot open email modal.');
    }
};

window.handleProfessorCorrectionAction = (professorId) => {
    if (!professorId) {
        // Try to get from Alpine component
        const component = document.querySelector('[x-data*="professorShow"]')?.__x?.$data;
        professorId = component?.professorId || null;
    }

    if (!professorId) {
        // Try to extract from URL
        const urlMatch = window.location.pathname.match(/\/professors\/(\d+)/);
        if (urlMatch) {
            professorId = parseInt(urlMatch[1]);
        }
    }

    if (!professorId) {
        console.error('Professor ID not found. Cannot open correction modal.');
        return;
    }

    if (window.Livewire && typeof window.Livewire.dispatch === 'function') {
        window.Livewire.dispatch('openModal', {
            component: 'features.professors.professor-correction-modal',
            arguments: { professor: professorId }
        });
    } else {
        console.error('Livewire is not available. Cannot open correction modal.');
    }
};

// Initialize comments system directly when script loads
// This ensures it works even if Alpine hasn't initialized yet
(function initializeCommentsDirectly() {
    function init() {
        // Only initialize if we're on a professor show page
        if (document.getElementById('comment-form')) {
            initComments();
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        // DOM is already ready
        init();
    }
})();

